import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Switch from "@mui/material/Switch";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmptyState from "../../shared/ui/EmptyState";
import ErrorState from "../../shared/ui/ErrorState";
import InactiveChip from "../../shared/ui/InactiveChip";
import PageHeader from "../../shared/ui/PageHeader";
import { useNotify } from "../../shared/ui/feedback/SnackbarProvider";
import { useAutoApplyFilters } from "../../shared/ui/filters/useAutoApplyFilters";
import DeactivateHolidayDialog from "./DeactivateHolidayDialog";
import { holidayErrorMessage } from "./errors";
import HolidayFormDialog from "./HolidayFormDialog";
import { useHolidayMutations, useHolidays } from "./hooks";
import { BR_UFS, Holiday, HolidaysQuery, KIND_LABEL, SCOPE_LABEL } from "./types";

type Filters = { year: string; uf: string; city: string; includeInactive: boolean };
const HEADERS = ["Data", "Nome", "Abrangência", "Tipo", "UF", "Cidade", "Fonte", "Situação", "Ações"];
export const SEEDED_HINT = "semeado — não editável";

const brDate = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}/${ymd.slice(0, 4)}`;
const toQuery = (f: Filters): HolidaysQuery => ({
  from: `${f.year}-01-01`,
  to: `${f.year}-12-31`,
  ...(f.uf ? { uf: f.uf } : {}),
  ...(f.city.trim() ? { city: f.city.trim() } : {}),
  ...(f.includeInactive ? { includeInactive: true } : {}),
});

/** Cadastro de feriados (S2, Admin): nacionais/estaduais semeados (cadeado) e municipais cadastrados aqui. */
export default function HolidaysPage({ now = new Date() }: { now?: Date }) {
  const year = now.getFullYear();
  const years = [year - 2, year - 1, year, year + 1, year + 2].map(String);
  const defaults: Filters = { year: String(year), uf: "", city: "", includeInactive: false };
  const [applied, setApplied] = useState<Filters>(defaults);
  const { draft, setField, bindText } = useAutoApplyFilters<Filters>({ value: applied, defaults, onApply: setApplied, textFields: ["city"], normalize: (f) => ({ ...f, city: f.city.trim() }) });
  const q = useHolidays(toQuery(applied));
  const { reactivate } = useHolidayMutations();
  const notify = useNotify();
  const [formOpen, setFormOpen] = useState(false);
  const [toDeactivate, setToDeactivate] = useState<Holiday | null>(null);

  const onReactivate = async (h: Holiday) => {
    try {
      await reactivate.mutateAsync(h.id);
      notify({ severity: "success", message: `Feriado “${h.name}” reativado.` });
    } catch (e) {
      notify({ severity: "error", message: holidayErrorMessage(e, "Não foi possível reativar o feriado.") });
    }
  };

  const rows = q.data ?? [];
  const body = () => {
    if (q.isLoading)
      return Array.from({ length: 5 }, (_, i) => (
        <TableRow key={i}>
          <TableCell colSpan={HEADERS.length}>
            <Skeleton data-testid="holidays-loading" />
          </TableCell>
        </TableRow>
      ));
    return rows.map((h) => (
      <TableRow key={h.id} hover sx={h.active ? undefined : { opacity: 0.6 }}>
        <TableCell sx={{ whiteSpace: "nowrap" }}>{brDate(h.date)}</TableCell>
        <TableCell>{h.name}</TableCell>
        <TableCell>{SCOPE_LABEL[h.scope]}</TableCell>
        <TableCell>{KIND_LABEL[h.kind]}</TableCell>
        <TableCell>{h.uf ?? "—"}</TableCell>
        <TableCell>{h.city ?? "—"}</TableCell>
        <TableCell sx={{ maxWidth: 220, fontSize: 12, color: "text.secondary" }}>{h.source}</TableCell>
        <TableCell>{h.active ? "Ativo" : <InactiveChip />}</TableCell>
        <TableCell align="right">
          {h.seeded ? (
            <Tooltip title={SEEDED_HINT}>
              <LockOutlinedIcon aria-label={SEEDED_HINT} fontSize="small" sx={{ color: "text.secondary", verticalAlign: "middle" }} />
            </Tooltip>
          ) : h.active ? (
            <Button size="small" color="error" onClick={() => setToDeactivate(h)} aria-label={`Desativar ${h.name}`}>
              Desativar
            </Button>
          ) : (
            <Button size="small" onClick={() => onReactivate(h)} disabled={reactivate.isPending} aria-label={`Reativar ${h.name}`}>
              Reativar
            </Button>
          )}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Feriados"
        description="Nacionais e estaduais (SP, ES) já vêm cadastrados. Cadastre aqui os municipais: eles ajustam o “esperado” do gráfico de calendário do Dashboard para as empresas daquela cidade."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
            Novo feriado municipal
          </Button>
        }
      />
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField select size="small" label="Ano" value={draft.year} onChange={(e) => setField("year", e.target.value)} sx={{ width: 110 }}>
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="UF" value={draft.uf} onChange={(e) => setField("uf", e.target.value)} sx={{ width: 110 }} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
          <MenuItem value="">Todas</MenuItem>
          {BR_UFS.map((uf) => (
            <MenuItem key={uf} value={uf}>
              {uf}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Cidade" {...bindText("city")} sx={{ width: 220 }} />
        <FormControlLabel control={<Switch checked={draft.includeInactive} onChange={(e) => setField("includeInactive", e.target.checked)} />} label="mostrar inativos" />
      </Paper>
      {q.isError && !q.data ? (
        <ErrorState title={q.error?.message} onRetry={() => q.refetch()} />
      ) : !q.isLoading && rows.length === 0 ? (
        <EmptyState title="Nenhum feriado com esses filtros." />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Feriados">
            <TableHead>
              <TableRow>
                {HEADERS.map((h) => (
                  <TableCell key={h} align={h === "Ações" ? "right" : undefined} sx={{ fontWeight: 700 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{body()}</TableBody>
          </Table>
        </TableContainer>
      )}
      <HolidayFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
      <DeactivateHolidayDialog holiday={toDeactivate} onClose={() => setToDeactivate(null)} />
    </Box>
  );
}
